import { useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'

import type { AgentV2ArtifactEditorRef } from '@/agent-v2/artifact-editor/components/AgentV2ArtifactEditor'
import { useAgentV2ArtifactExport } from '@/agent-v2/artifact-templates/hooks/useAgentV2ArtifactExport'
import { ExportDocxButton } from '@/agent-v2/docx-export/components/ExportDocxButton'
import { useOpenDocxExportsFolder } from '@/agent-v2/docx-export/hooks/useOpenDocxExportsFolder'
import { AddTemplateButton } from '@/artifact-templates/components/AddTemplateButton'
import { ApplyingTemplateModal } from '@/artifact-templates/components/ApplyingTemplateModal'
import { SelectTemplateModal, SELECT_TEMPLATE_MODAL_TAB, type SelectTemplateModalInitialTab } from '@/artifact-templates/components/SelectTemplateModal'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { AuthContext } from '@/context/auth-context'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { kMatterArtifactNameFallback } from '@/matter-artifacts/constants'
import { selectCurrentMatterArtifactVersionFromVersionId } from '@/matter-artifacts/store/selectors'
import type { RootState } from '@/store/store'
import { useAppSelector } from '@/store/store-hooks'
import { DraftExportContext, type DraftExportScope } from './draft-export-context'
import { useDraftWorkspace } from '../hooks/useDraftWorkspace'

const kDocxExportTemplateHint =
  'Export as .docx creates a Word document with this template. It takes a few minutes and is saved to Matter Files › Draft .docx exports.'

/**
 * Supplies the open draft's export surface to the draft header and the editor toolbar, and mounts
 * the template dialogs for both.
 */
export default function DraftExportProvider({ children }: { children: ReactNode }) {
  // One component, never a branch on the element type: this wraps the dashboard's whole content
  // subtree, so swapping what renders here would unmount the outlet and drop the live conversation.
  // With no draft open the export state stays disabled instead.
  const { draftArtifactVersionId } = useDraftWorkspace()
  const { user } = useContext(AuthContext)
  const { matterId, chatId } = useParams<{ matterId: string; chatId: string }>()
  const [selectTemplateOpen, setSelectTemplateOpen] = useState(false)
  const [initialSelectTemplateTab, setInitialSelectTemplateTab] = useState<SelectTemplateModalInitialTab>(SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES)
  const currentMatterArtifactVersion = useAppSelector((state: RootState) => selectCurrentMatterArtifactVersionFromVersionId(state, draftArtifactVersionId))
  const canEdit = Boolean(user?.uid && currentMatterArtifactVersion?.created_by_user_id === user.uid)
  const draftName = currentMatterArtifactVersion?.name || kMatterArtifactNameFallback

  // Everything template-shaped — changing it, and exporting through it — is creator-only. A
  // collaborator's only export is the header's plain download of the draft as they see it.
  const templatesEnabled = !!matterId && !!chatId && !!currentMatterArtifactVersion && kGetFeatureFlagStatus(kFeatureFlags.ARTIFACT_TEMPLATES)
  const canMutateTemplate = templatesEnabled && canEdit
  // The one switch between the two ways of applying a template. On: Export as .docx runs as a job
  // and reports in the chat, and downloads and saved copies are the plain draft. Off: the template
  // is applied inside the download or save request, as before. Until the flag loads it reads as off,
  // so the creator's download and save wait rather than start the old path.
  const { enabled: docxExportEnabled, loading: docxExportLoading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.DOCX_EXPORT)
  const openDocxExportsFolder = useOpenDocxExportsFolder(matterId, chatId)

  // Clicking an export action blurs the editor, which starts a fire-and-forget autosave. Exports
  // flush through this ref so they never deliver the pre-save head.
  const editorRef = useRef<AgentV2ArtifactEditorRef>(null)
  const flushEditorPendingSave = useCallback(() => editorRef.current?.flushPendingSave() ?? Promise.resolve(null), [])

  const {
    template,
    uploadingTemplateName,
    isTemplateApplied,
    isExporting,
    applyTemplateModalOpen,
    applyStatus,
    outputFileId,
    onCancelApply,
    onCloseApplyModal,
    onRetry,
    onSaveWithoutTemplate,
    onDownloadWithoutTemplate,
    onViewFile,
    onDownloadWithTemplate,
    onSave,
    onSavePlain,
    onTemplateUpload,
    onTemplateSelect,
  } = useAgentV2ArtifactExport({
    matterArtifactVersionId: currentMatterArtifactVersion?.id ?? draftArtifactVersionId ?? '',
    matterId: matterId ?? null,
    title: draftName,
    conversationId: chatId ?? '',
    enabled: templatesEnabled,
    mutationsEnabled: canMutateTemplate,
    prepareExport: canEdit ? flushEditorPendingSave : undefined,
  })

  const scope: DraftExportScope = useMemo(
    () => ({
      templateButton: canMutateTemplate ? (
        <>
          <AddTemplateButton
            template={template}
            uploadingName={uploadingTemplateName}
            applied={!docxExportEnabled && isTemplateApplied}
            matterId={matterId}
            onUpload={onTemplateUpload}
            onOpenSelectTemplate={(initialTab = SELECT_TEMPLATE_MODAL_TAB.MATTER_FILES) => {
              setInitialSelectTemplateTab(initialTab)
              setSelectTemplateOpen(true)
            }}
            onRemove={() => onTemplateSelect(undefined)}
            hint={docxExportEnabled ? kDocxExportTemplateHint : undefined}
          />
          {docxExportEnabled && template && !uploadingTemplateName && chatId && currentMatterArtifactVersion && (
            <ExportDocxButton
              conversationId={chatId}
              matterArtifactVersionId={currentMatterArtifactVersion.id}
              templateUserFileId={template.fileId}
              prepareExport={flushEditorPendingSave}
            />
          )}
        </>
      ) : null,
      editorRef,
      onDownloadWithTemplate: templatesEnabled && !docxExportEnabled ? onDownloadWithTemplate : undefined,
      onSave: templatesEnabled ? (docxExportEnabled ? onSavePlain : onSave) : undefined,
      onViewExports: templatesEnabled && docxExportEnabled ? openDocxExportsFolder : undefined,
      isExporting: templatesEnabled && isExporting,
      isLoadingExportActions: canMutateTemplate && docxExportLoading,
    }),
    [
      canMutateTemplate,
      chatId,
      currentMatterArtifactVersion,
      docxExportEnabled,
      docxExportLoading,
      flushEditorPendingSave,
      onSavePlain,
      openDocxExportsFolder,
      isExporting,
      isTemplateApplied,
      matterId,
      onDownloadWithTemplate,
      onSave,
      onTemplateSelect,
      onTemplateUpload,
      template,
      templatesEnabled,
      uploadingTemplateName,
    ]
  )

  return (
    <DraftExportContext.Provider value={scope}>
      {children}
      {/* The dialogs only serve template mutations, so they never mount for read-only users. */}
      {canMutateTemplate && (
        <>
          <ApplyingTemplateModal
            open={applyTemplateModalOpen}
            outputFileName={`${draftName}.docx`}
            outputFileId={outputFileId}
            status={applyStatus}
            templateName={template?.name ?? ''}
            successLabel={onViewFile ? 'Saved to Matter Files' : 'Downloaded'}
            onCancel={onCancelApply}
            onSaveWithoutTemplate={onSaveWithoutTemplate}
            onDownloadWithoutTemplate={onDownloadWithoutTemplate}
            onDismiss={onCloseApplyModal}
            onRetry={onRetry}
            onViewFile={onViewFile}
          />
          <SelectTemplateModal
            open={selectTemplateOpen}
            matterId={matterId ?? ''}
            initialTab={initialSelectTemplateTab}
            template={template}
            onClose={() => setSelectTemplateOpen(false)}
            onSelect={(selected) => {
              onTemplateSelect(selected)
              setSelectTemplateOpen(false)
            }}
          />
        </>
      )}
    </DraftExportContext.Provider>
  )
}
